class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
    this.setupCustomUi();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  setupCustomUi() {
    this.initialStateElement = this.querySelector('[data-search-initial-state]');
    this.initialStateConfigElement = this.querySelector('[data-search-initial-config]');
    this.initialStateRendered = false;
    this.initialStateLoading = false;
    this.initialStateConfig = null;
    this.initialStateProductCache = {};

    this.bindInitialStateEvents();
    this.bindPopularSearches();
    this.updateUiState();
  }
  bindInitialStateEvents() {
    if (!this.initialStateElement || !this.initialStateConfigElement) return;

    const details = this.closest('details');

    if (details && details.dataset.searchInitialStateBound !== 'true') {
      details.dataset.searchInitialStateBound = 'true';

      details.addEventListener('toggle', () => {
        if (details.hasAttribute('open')) {
          this.renderInitialState();
        }
      });
    }

    this.input.addEventListener('focus', () => {
      if (!this.getQuery().length) {
        this.renderInitialState();
      }
    });
  }

  getInitialStateConfig() {
    if (this.initialStateConfig) return this.initialStateConfig;
    if (!this.initialStateConfigElement) return null;

    try {
      this.initialStateConfig = JSON.parse(this.initialStateConfigElement.textContent || '{}');
      return this.initialStateConfig;
    } catch (error) {
      console.warn('Search initial state config could not be parsed.', error);
      return null;
    }
  }

  renderInitialState() {
    if (!this.initialStateElement || this.initialStateRendered || this.initialStateLoading) return;

    const config = this.getInitialStateConfig();
    if (!config) return;

    const popularSearches = Array.isArray(config.popularSearches) ? config.popularSearches : [];
    const bestsellerHandles = Array.isArray(config.bestsellerHandles) ? config.bestsellerHandles : [];

    this.initialStateLoading = true;

    const popularMarkup = this.buildPopularSearchesMarkup(popularSearches);
    const productRequests = bestsellerHandles
      .slice(0, 4)
      .map((handle) => this.fetchInitialStateProduct(handle).catch(() => null));

    Promise.all(productRequests)
      .then((products) => {
        const validProducts = products.filter(Boolean);
        const bestsellersMarkup = this.buildBestsellersMarkup(validProducts, bestsellerHandles, config);

        this.initialStateElement.innerHTML = `${popularMarkup}${bestsellersMarkup}`;
        this.initialStateRendered = true;
        this.initialStateLoading = false;

        this.bindPopularSearches();
        this.updateUiState();
      })
      .catch((error) => {
        this.initialStateLoading = false;
        console.warn('Search initial state could not be rendered.', error);
      });
  }

  fetchInitialStateProduct(handle) {
    const cleanHandle = String(handle || '').trim();
    if (!cleanHandle) return Promise.resolve(null);

    if (this.initialStateProductCache[cleanHandle]) {
      return Promise.resolve(this.initialStateProductCache[cleanHandle]);
    }

    const localeRoot = window.Shopify?.routes?.root || '/';
    const normalizedLocaleRoot = localeRoot.endsWith('/') ? localeRoot : `${localeRoot}/`;

    return fetch(`${normalizedLocaleRoot}products/${encodeURIComponent(cleanHandle)}.js`)
      .then((response) => {
        if (!response.ok) return null;
        return response.json();
      })
      .then((product) => {
        if (!product) return null;

        const normalizedProduct = {
          ...product,
          handle: cleanHandle,
        };

        this.initialStateProductCache[cleanHandle] = normalizedProduct;
        return normalizedProduct;
      });
  }

  buildPopularSearchesMarkup(popularSearches) {
    if (!popularSearches.length) return '';

    const links = popularSearches
      .filter((item) => item && item.label)
      .map((item) => {
        const label = this.escapeHtml(item.label);
        const url = this.escapeHtml(item.url || `/search?q=${encodeURIComponent(item.label)}`);

        return `
          <a href="${url}" class="ct-search-modal__popular-link">
            ${label}
          </a>
        `;
      })
      .join('');

    if (!links) return '';

    return `
      <div class="ct-search-modal__popular">
        <div class="ct-search-modal__popular-list">
          ${links}
        </div>
      </div>
    `;
  }

  buildBestsellersMarkup(products, handles, config) {
    if (!products.length) return '';

    const bestSellingProductsTitle = this.escapeHtml(
      config?.translations?.bestSellingProducts || 'Prodotti più venduti'
    );

    const desktopCards = products
      .slice(0, 4)
      .map((product) => this.buildBestsellerCard(product, config))
      .join('');

    const mobileCards = products
      .slice(0, 2)
      .map((product) => this.buildBestsellerCard(product, config))
      .join('');

    return `
      <div class="ct-search-bestsellers ct-search-bestsellers--desktop">
        <h3 class="ct-search-bestsellers__title">${bestSellingProductsTitle}</h3>

        <div class="ct-search-bestsellers__grid">
          ${desktopCards}
        </div>
      </div>

      <div class="ct-search-bestsellers ct-search-bestsellers--mobile">
        <h3 class="ct-search-bestsellers__title">${bestSellingProductsTitle}</h3>

        <div class="ct-search-bestsellers__grid">
          ${mobileCards}
        </div>
      </div>
    `;
  }

  buildBestsellerCard(product, config) {
    const title = this.escapeHtml(product.title || '');
    const url = this.escapeHtml(product.url || `/products/${product.handle}`);
    const imageUrl = this.escapeHtml(this.getProductImageUrl(product));
    const imageAlt = this.escapeHtml(product.title || '');
    const price = this.escapeHtml(this.getProductPriceLabel(product, config));
    const badgeMarkup = this.productHasBestSellerTag(product) ? this.buildBestSellerBadgeMarkup(config) : '';

    return `
      <a href="${url}" class="ct-search-bestsellers__card ct-search-card">
        ${
          imageUrl
            ? `
              <span class="ct-search-card__media">
                <img
                  src="${imageUrl}"
                  alt="${imageAlt}"
                  loading="lazy"
                  width="320"
                  height="320"
                >
                ${badgeMarkup}
              </span>
            `
            : ''
        }

        <span class="ct-search-card__title">${title}</span>

        <span class="ct-search-card__price">
          ${price}
        </span>
      </a>
    `;
  }

  buildBestSellerBadgeMarkup(config) {
    if (!config?.badgeRotatingUrl || !config?.badgeTextUrl) return '';

    return `
      <span class="ct-product-best-seller-badge ct-product-best-seller-badge--search" aria-hidden="true">
        <span class="ct-product-best-seller-badge__rotating">
          <img
            src="${this.escapeHtml(config.badgeRotatingUrl)}"
            alt=""
            loading="lazy"
            width="350"
            height="350"
          >
        </span>

        <span class="ct-product-best-seller-badge__text">
          <img
            src="${this.escapeHtml(config.badgeTextUrl)}"
            alt=""
            loading="lazy"
            width="350"
            height="350"
          >
        </span>
      </span>
    `;
  }

  productHasBestSellerTag(product) {
    const tags = product.tags || [];

    if (Array.isArray(tags)) {
      return tags.map((tag) => String(tag).toLowerCase()).includes('best-seller');
    }

    return String(tags)
      .split(',')
      .map((tag) => tag.trim().toLowerCase())
      .includes('best-seller');
  }

  getProductImageUrl(product) {
    const featuredImage = product.featured_image || product.featured_image?.src || product.images?.[0];

    if (!featuredImage) return '';

    if (typeof featuredImage === 'string') {
      return featuredImage;
    }

    return featuredImage.src || '';
  }

  getProductPriceLabel(product, config) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const prices = variants
      .map((variant) => Number(variant.price))
      .filter((price) => Number.isFinite(price));

    const minPrice = prices.length ? Math.min(...prices) : Number(product.price || 0);
    const maxPrice = prices.length ? Math.max(...prices) : Number(product.price || 0);

    if (minPrice && maxPrice && minPrice !== maxPrice) {
      return `Da ${this.formatMoney(minPrice, config?.currency)}`;
    }

    return this.formatMoney(minPrice || maxPrice || 0, config?.currency);
  }

  formatMoney(cents, currency = 'EUR') {
    const numericCents = Number(cents) || 0;

    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency,
    }).format(numericCents / 100);
  }

  escapeHtml(value) {
    const element = document.createElement('div');
    element.textContent = value == null ? '' : String(value);
    return element.innerHTML;
  }

  bindPopularSearches() {
    this.querySelectorAll('[data-popular-search]').forEach((link) => {
      if (link.dataset.popularSearchBound === 'true') return;
      link.dataset.popularSearchBound = 'true';

      link.addEventListener('click', (event) => {
        const searchValue = link.dataset.popularSearch || '';
        if (!searchValue) return;

        event.preventDefault();
        this.input.value = searchValue;
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
        this.input.focus();
      });
    });
  }

  initRenderedCustomUi() {
    this.bindTabs();
    this.bindRails();
    this.updateUiState();

    requestAnimationFrame(() => this.resetRails());
  }

  resetRails() {
    this.querySelectorAll('[data-search-rail]').forEach((rail) => {
      rail.scrollLeft = 0;
    });
  }

bindTabs() {
  const root = this.querySelector('[data-search-results-root]');
  if (!root) return;

  const tabs = root.querySelectorAll('[data-search-tab]');
  const panels = root.querySelectorAll('[data-search-tab-panel]');
  if (!tabs.length || !panels.length) return;

  tabs.forEach((tab) => {
    if (tab.dataset.searchTabBound === 'true') return;
    tab.dataset.searchTabBound = 'true';

    tab.addEventListener('click', () => {
      const tabName = tab.dataset.searchTab;

      tabs.forEach((item) => {
        const isActive = item.dataset.searchTab === tabName;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      panels.forEach((panel) => {
        const isActive = panel.dataset.searchTabPanel === tabName;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });

      requestAnimationFrame(() => this.updateTabIndicator(root));
    });
  });

  this.bindTabIndicatorResize();
  requestAnimationFrame(() => this.updateTabIndicator(root));
}

updateTabIndicator(root = this.querySelector('[data-search-results-root]')) {
  const tabList = root?.querySelector('.ct-search-results__tabs');
  if (!tabList) return;

  const activeTab = tabList.querySelector('[data-search-tab].is-active');
  if (!activeTab) return;

  const tabListRect = tabList.getBoundingClientRect();
  const activeTabRect = activeTab.getBoundingClientRect();

  tabList.style.setProperty('--ct-tab-indicator-left', `${activeTabRect.left - tabListRect.left}px`);
  tabList.style.setProperty('--ct-tab-indicator-width', `${activeTabRect.width}px`);
}

bindTabIndicatorResize() {
  if (this.tabIndicatorResizeBound === true) return;
  this.tabIndicatorResizeBound = true;

  window.addEventListener('resize', () => {
    window.clearTimeout(this.tabIndicatorResizeTimeout);

    this.tabIndicatorResizeTimeout = window.setTimeout(() => {
      this.updateTabIndicator();
    }, 100);
  });
}

  bindRails() {
    const panels = this.querySelectorAll('[data-search-tab-panel]');

    panels.forEach((panel) => {
      if (panel.dataset.searchRailBound === 'true') return;
      panel.dataset.searchRailBound = 'true';

      const rail = panel.querySelector('[data-search-rail]');
      const prev = panel.querySelector('[data-search-arrow-prev]');
      const next = panel.querySelector('[data-search-arrow-next]');

      if (!rail || !prev || !next) return;

      const getScrollAmount = () => {
        const firstSlide = rail.querySelector('.ct-search-results__slide');
        if (!firstSlide) return rail.clientWidth * 0.8;

        const railStyles = window.getComputedStyle(rail);
        const gap = parseFloat(railStyles.columnGap || railStyles.gap || 0);

        return firstSlide.getBoundingClientRect().width + gap;
      };

      prev.addEventListener('click', () => {
        rail.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' });
      });

      next.addEventListener('click', () => {
        rail.scrollBy({ left: getScrollAmount(), behavior: 'smooth' });
      });
    });
  }

  hasRenderableResults() {
    const hasSuggestions = !!this.querySelector('.ct-search-suggestions__item');
    const hasTabs = !!this.querySelector('[data-search-tab]');
    const hasSlides = !!this.querySelector('.ct-search-results__slide');

    return hasSuggestions || hasTabs || hasSlides;
  }

  updateUiState() {
    const hasQuery = this.getQuery().length > 0;
    const hasRenderableResults = this.hasRenderableResults();

    if (this.initialStateElement) {
      this.initialStateElement.hidden = hasQuery;
    }

    const modal = this.closest('.ct-search-modal');
    if (modal) {
      modal.classList.toggle('is-searching', hasQuery && hasRenderableResults);
    }

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.classList.toggle('is-active', hasQuery && hasRenderableResults);
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      this.querySelector('[data-search-results-root]')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;
    this.updateUiState();

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);

    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      this.updateUiState();
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.renderInitialState();
      this.updateUiState();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;

    if (currentButtonText) {
      if (previousTerm && currentButtonText.match(new RegExp(previousTerm, 'g'))?.length > 1) {
        return;
      }

      const fallbackText = this.getInitialStateConfig()?.translations?.viewAll || 'Vedi tutto';

      if (!previousTerm) {
        searchForTextElement.innerText = fallbackText;
        return;
      }

      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText || fallbackText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );

    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;

        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });

        this.renderSearchResults(resultsMarkup);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.code === 20) return;
        this.close();
        throw error;
      });
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    if (this.hasRenderableResults()) {
      this.setAttribute('results', true);
    } else {
      this.removeAttribute('results');
    }

    this.setLiveRegionResults();
    this.initRenderedCustomUi();
    this.open();
  }

  setLiveRegionResults() {
    const liveRegionValue = this.querySelector('[data-predictive-search-live-region-count-value]');
    this.removeAttribute('loading');

    if (liveRegionValue) {
      this.setLiveRegionText(liveRegionValue.textContent);
    }
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }

    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    this.updateUiState();
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    this.updateUiState();
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');

      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.innerHTML = '';
      }
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.removeAttribute('style');
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);

